// src/components/LoginPage.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../axiosInstance';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { UserContext } from '../contexts/UserContext';

const translations = {
  en: {
    title: 'Adey GAMES!',
    loginPrompt: 'Log in to your account',
    phoneLabel: 'Phone Number',
    phonePlaceholder: 'e.g., +1234567890 or 0912345678',
    passwordLabel: 'Password',
    passwordPlaceholder: '••••••••',
    loginButton: 'Log in',
    loggingIn: 'Logging in...',
    forgotPassword: 'Forgot Password?',
    signupPrompt: "Don't have an account? Sign up",
    errorTitle: 'Error!',
    sessionExpired: 'Your session has expired. Please log in again.',
    enterBoth: 'Please enter both phone number and password.',
    loginFailed: 'Login failed. Please check your credentials.',
  },
  am: {
    title: 'አደይ ጨዋታዎች!',
    loginPrompt: 'ወደ መለያዎ ይግቡ',
    phoneLabel: 'ስልክ ቁጥር',
    phonePlaceholder: 'ለምሳሌ፣ +1234567890 ወይም 0912345678',
    passwordLabel: 'የይለፍ ቃል',
    passwordPlaceholder: '••••••••',
    loginButton: 'ግባ',
    loggingIn: 'በመግባት ላይ...',
    forgotPassword: 'የይለፍ ቃልን ረስተዋል?',
    signupPrompt: 'መለያ የለዎትም? ይመዝገቡ',
    errorTitle: 'ስህተት!',
    sessionExpired: 'እርስዎ ከመቆያው ወጥተዋል። እባክዎ ደግመው ይግቡ።',
    enterBoth: 'እባክዎ ሁለቱንም ስልክ ቁጥርና የይለፍ ቃል ያስገቡ።',
    loginFailed: 'መግባት አልተሳካም። እባክዎ መረጃዎትን ይክረዱ።',
  },
  ti: {
    title: 'ስሳቴክ ጨዋታታት!',
    loginPrompt: 'ናይ መለያካ ውሰድ',
    phoneLabel: 'ቁጽሪ ስልኪ',
    phonePlaceholder: 'ለናይ ናምሓራ፣ +1234567890 ወይ 0912345678',
    passwordLabel: 'መሕለፊ ቓል',
    passwordPlaceholder: '••••••••',
    loginButton: 'ኣቐምጥ',
    loggingIn: 'እዩ ዝኣተወ...',
    forgotPassword: 'መሕለፊ ቓል ረሳኻ?',
    signupPrompt: 'መለያ ኣይብሉኻን? መዝግብ',
    errorTitle: 'ጌጋ!',
    sessionExpired: 'ናይ ስብሓት ግዜ ኣብቲ እዩ ዝተዛወረ።',
    enterBoth: 'ቁጽሪ ስልኪን መሕለፊ ቓልን ያስገቡ።',
    loginFailed: 'መእተዊ ኣይተሳናተ። ናይካ መረጃ ምርምር አድርጉ።',
  },
  om: {
    title: 'Adey TAPHOOTA!',
    loginPrompt: 'Akkaawuntii kee seeni',
    phoneLabel: 'Lakkoofsa bilbilaa',
    phonePlaceholder: 'fakkeenya, +1234567890 ykn 0912345678',
    passwordLabel: 'Jecha iccitii',
    passwordPlaceholder: '••••••••',
    loginButton: 'Seeni',
    loggingIn: 'Seenaa jira...',
    forgotPassword: 'Jecha iccitii irra deebi’i?',
    signupPrompt: 'Akkaawunti hin qabduu? Galmaa’i',
    errorTitle: 'Dogoggora!',
    sessionExpired: 'Yeroo turtii keetii xumurameera. Deebi’i seeni.',
    enterBoth: 'Lakkoofsa bilbilaa fi jecha iccitii galchi.',
    loginFailed: 'Seensuu hin milkoofne. Ragaa kee mirkaneessi.',
  },
  we: {
    title: 'A WASHTTO!',
    loginPrompt: 'Akkaawuntanau gishsha',
    phoneLabel: 'Gita Bilbila',
    phonePlaceholder: 'e.g., +1234567890 or 0912345678',
    passwordLabel: 'Hufanna kala',
    passwordPlaceholder: '••••••••',
    loginButton: 'Gishsha',
    loggingIn: 'Gishshuwa...',
    forgotPassword: 'Hufanna kala doshtte?',
    signupPrompt: 'Akkaawunta doshsha? Eettaa',
    errorTitle: 'Ashsha!',
    sessionExpired: 'Qommo qoma gishsha daraga. Eettaa gishsha.',
    enterBoth: 'Bilbila gita bolla hufanna kala naani.',
    loginFailed: 'Gishshuwa aydara. Makkittanau bullaa.',
  }
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUserData } = useContext(UserContext);

  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'en');
  const t = translations[language];

  const [loginPhoneNumber, setLoginPhoneNumber] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Function to validate phone number format for any country, mirroring backend logic
  const isValidPhoneNumber = (number) => {
    if (!number) return false;
    let phone = number.trim();

    // If it starts with '+', treat it as an international number
    if (phone.startsWith('+')) {
      const cleaned = '+' + phone.slice(1).replace(/\D/g, ''); // Keep '+' and remove non-digits
      // Basic length validation for international numbers (e.g., +1234567890)
      // A typical international number is between 8 and 16 digits (excluding '+')
      if (cleaned.length >= 9 && cleaned.length <= 17) { // '+' and 8-16 digits
          return true;
      }
      return false; // Invalid length for an international number
    }

    // Handle local Ethiopian numbers (assuming they don't start with '+')
    let cleanedPhone = phone.replace(/\D/g, ''); // Remove all non-digits

    // Convert 09... or 07... to 2519... or 2517...
    if (/^09\d{8}$/.test(cleanedPhone) || /^07\d{8}$/.test(cleanedPhone)) {
      return true;
    }
    // Already in 251 format
    if (/^2519\d{8}$/.test(cleanedPhone) || /^2517\d{8}$/.test(cleanedPhone)) {
      return true;
    }

    // If it's a 9-digit number starting with 9 or 7 (e.g., 912345678 without leading 0), assume Ethiopian
    if (/^(9|7)\d{8}$/.test(cleanedPhone) && cleanedPhone.length === 9) {
      return true;
    }

    // For any other format, return false. This is a strict approach to avoid
    // storing ambiguous or incomplete phone numbers.
    return false;
  };

  // Auto-login for Telegram WebApp
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const userId = params.get('userId');

    if (window.Telegram?.WebApp && token && userId) {
      localStorage.setItem('token', token);
      localStorage.setItem('userId', userId);
      refreshUserData();
      navigate('/dashboard');
    }
  }, [location.search, navigate, refreshUserData]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('session') === 'expired') {
      toast.info(t.sessionExpired, { position: 'top-center', autoClose: 5000 });
    }
  }, [location.search, t]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);

    if (!loginPhoneNumber || !loginPassword) {
      toast.error(t.enterBoth);
      setErrorMessage(t.enterBoth);
      setLoading(false);
      return;
    }

    // Frontend validation before sending to backend
    if (!isValidPhoneNumber(loginPhoneNumber)) {
      toast.error(t.phonePlaceholder); // Use placeholder text as error message for invalid format
      setErrorMessage(t.phonePlaceholder);
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post('/auth/login', {
        phoneNumber: loginPhoneNumber,
        password: loginPassword,
      });

      if (response.data?.token && response.data?.userId) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('userId', response.data.userId);

        refreshUserData();
        toast.success('Login successful! Redirecting...');
        navigate('/dashboard');
      } else {
        toast.error(t.loginFailed);
        setErrorMessage(t.loginFailed);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Network error';
      toast.error(msg);
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    setLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
      <ToastContainer />
      <div className="w-full max-w-md bg-white rounded-tr-[6rem] shadow-lg overflow-hidden">
        <div className="relative bg-[#1570EF] h-44 flex flex-col items-center justify-center p-6 rounded-tr-[6rem]">
          <h2 className="text-3xl font-bold text-white text-center">{t.title}</h2>
        </div>

        <div className="p-8">
          <p className="text-center text-gray-600 mb-6">{t.loginPrompt}</p>

          {errorMessage && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative mb-4">
              <strong className="font-bold">{t.errorTitle}</strong>
              <span className="block sm:inline"> {errorMessage}</span>
            </div>
          )}

          <form onSubmit={handleLoginSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.phoneLabel}</label>
              <input
                type="tel"
                value={loginPhoneNumber}
                onChange={(e) => setLoginPhoneNumber(e.target.value)}
                placeholder={t.phonePlaceholder}
                className="w-full px-4 py-3 rounded-md border border-gray-300 bg-blue-50 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.passwordLabel}</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder={t.passwordPlaceholder}
                className="w-full px-4 py-3 rounded-md border border-gray-300 bg-blue-50 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full ${loading ? 'bg-gray-400' : 'bg-[#1570EF] hover:bg-[#155ed6]'} text-white font-semibold py-3 rounded-md transition duration-300`}
            >
              {loading ? t.loggingIn : t.loginButton}
            </button>
          </form>

          <div className="mt-6 flex flex-col items-center text-sm space-y-1">
            <button
              onClick={() => navigate('/forgot-password')}
              className="text-[#1570EF] hover:underline font-medium"
              disabled={loading}
            >
              {t.forgotPassword}
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="text-[#1570EF] hover:underline font-medium"
              disabled={loading}
            >
              {t.signupPrompt}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
